/**
 * HelpSearch Component
 * Searchable knowledge base with instant filtering
 */

import React, { useState, useMemo, useCallback } from 'react';
import { Search, X, FileText, BookOpen, Lightbulb, GitBranch, ArrowRight } from 'lucide-react';
import { searchIndex, searchSynonyms } from '../../content/helpContent';
import { cn } from '../../lib/utils';

const categoryIcons = {
  'Pages': FileText,
  'Key Concepts': Lightbulb,
  'Modules': BookOpen,
  'Decision Frameworks': GitBranch,
  'Investment Workflow': ArrowRight,
};

const categoryColors = {
  'Pages': 'bg-blue-500/10 text-blue-600 border-blue-200',
  'Key Concepts': 'bg-amber-500/10 text-amber-600 border-amber-200',
  'Modules': 'bg-emerald-500/10 text-emerald-600 border-emerald-200',
  'Decision Frameworks': 'bg-purple-500/10 text-purple-600 border-purple-200',
  'Investment Workflow': 'bg-slate-500/10 text-slate-600 border-slate-200',
};

export const HelpSearch = ({ onResultClick, className }) => {
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  // Expand query with synonyms
  const expandedTerms = useMemo(() => {
    if (!query.trim()) return [];
    
    const terms = query.toLowerCase().split(/\s+/).filter(t => t.length > 1);
    const expanded = new Set(terms);
    
    terms.forEach(term => {
      Object.entries(searchSynonyms).forEach(([key, synonyms]) => {
        if (key.includes(term) || synonyms.some(s => s.includes(term))) {
          expanded.add(key);
          synonyms.forEach(s => expanded.add(s));
        }
      });
    });
    
    return Array.from(expanded);
  }, [query]);

  // Search results
  const results = useMemo(() => {
    if (expandedTerms.length === 0) return [];

    const scored = searchIndex.map(item => {
      let score = 0;
      const titleLower = item.title.toLowerCase();
      const descLower = item.description.toLowerCase();
      
      expandedTerms.forEach(term => {
        // Title matches (higher weight)
        if (titleLower.includes(term)) score += 10;
        if (titleLower.startsWith(term)) score += 5;
        
        // Keyword matches
        if (item.keywords.some(k => k.includes(term))) score += 5;
        
        // Description matches
        if (descLower.includes(term)) score += 2;
      });
      
      return { ...item, score };
    });

    return scored
      .filter(item => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 12);
  }, [expandedTerms]);

  // Group results by category
  const groupedResults = useMemo(() => {
    const groups = {};
    results.forEach(result => {
      if (!groups[result.category]) {
        groups[result.category] = [];
      }
      groups[result.category].push(result);
    });
    return groups;
  }, [results]);

  const handleResultClick = useCallback((result) => {
    setQuery('');
    onResultClick?.(result);
  }, [onResultClick]);

  const highlightMatch = (text, terms) => {
    if (!terms.length) return text;
    
    const regex = new RegExp(`(${terms.join('|')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, i) => 
      terms.some(t => part.toLowerCase() === t.toLowerCase()) ? (
        <mark key={i} className="bg-yellow-200 text-yellow-900 rounded px-0.5">
          {part}
        </mark>
      ) : part
    );
  };

  return (
    <div className={cn("relative", className)}>
      {/* Search Input */}
      <div className="relative">
        <Search 
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" 
          size={18} 
        />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setTimeout(() => setIsFocused(false), 200)}
          placeholder="Search help topics, concepts, modules..."
          className="w-full pl-10 pr-10 py-3 bg-background border border-border rounded-lg text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
          data-testid="help-search-input"
        />
        {query && (
          <button
            onClick={() => setQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Results Dropdown */}
      {isFocused && query && (
        <div 
          className="absolute top-full left-0 right-0 mt-2 bg-background border border-border rounded-lg shadow-lg max-h-[400px] overflow-y-auto z-50"
          data-testid="help-search-results"
        >
          {results.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground text-sm">
              No results found for "{query}"
            </div>
          ) : (
            <div className="py-2">
              {Object.entries(groupedResults).map(([category, items]) => {
                const Icon = categoryIcons[category] || FileText;
                
                return (
                  <div key={category}>
                    <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide bg-muted/50">
                      {category}
                    </div>
                    {items.map((result) => (
                      <button
                        key={result.id}
                        onClick={() => handleResultClick(result)}
                        className="w-full px-3 py-2 text-left hover:bg-muted/50 flex items-start gap-3 transition-colors"
                      >
                        <div className={cn(
                          "mt-0.5 p-1.5 rounded border",
                          categoryColors[category]
                        )}>
                          <Icon size={14} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm">
                            {highlightMatch(result.title, expandedTerms)}
                          </div>
                          <div className="text-xs text-muted-foreground line-clamp-1">
                            {highlightMatch(result.description, expandedTerms)}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HelpSearch;
